import { type ConvertedVideo, type ConvertVideoOptions, convertVideo } from "mediatools";
import { baseName } from "../_components/format";
import type { ProcessedVideo } from "../_components/VideoResultPanel";
import type { RunContext } from "./types";

/** Wrap a {@link ConvertedVideo} from the package into a downloadable result. */
export function toVideoResult(
  out: ConvertedVideo,
  sourceName: string,
  suffix?: string
): ProcessedVideo {
  const base = baseName(sourceName);
  return {
    blob: new Blob([out.data], { type: out.mimeType }),
    baseName: suffix ? `${base}-${suffix}` : base,
    extension: out.extension,
  };
}

/**
 * Shared `run` body for the convert/compress/mute/rotate video tools: run
 * {@link convertVideo} (wiring up progress), then shape the result. The four
 * tools differ only in which `ConvertVideoOptions` they pass and the filename
 * suffix, so the boilerplate lives here once.
 */
export async function runVideoConversion(
  ctx: RunContext,
  options: Omit<ConvertVideoOptions, "onProgress">,
  suffix?: string
): Promise<ProcessedVideo> {
  if (!ctx.file) throw new Error("No file selected.");
  const out = await convertVideo(ctx.file, {
    ...options,
    ...(ctx.onProgress ? { onProgress: ctx.onProgress } : {}),
  });
  return toVideoResult(out, ctx.file.name, suffix);
}
